import { useEffect, useRef, useState } from 'react';

const FONT = "'DM Sans', sans-serif";
const FONT_DISPLAY = "'Source Serif 4', 'DM Sans', serif";

type Props = {
  variante: 'A' | 'B';
  audioUrl: string | null;
  nomeHomenageada: string;
  selected?: boolean;
  onPlayingChange?: (playing: boolean) => void;
};

const VARIANT_LABEL: Record<'A' | 'B', { ord: string; longa: string }> = {
  A: { ord: 'I', longa: 'Versão um' },
  B: { ord: 'II', longa: 'Versão dois' },
};

export default function PreviewCard({ variante, audioUrl, nomeHomenageada, selected = false, onPlayingChange }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setCurrent(a.currentTime || 0);
      setProgress(a.duration ? a.currentTime / a.duration : 0);
    };
    const onLoaded = () => {
      setDuration(a.duration || 0);
      setLoaded(true);
    };
    const onEnded = () => {
      setPlaying(false);
      onPlayingChange?.(false);
    };
    const onErr = () => setError(true);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onErr);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onErr);
    };
  }, [onPlayingChange]);

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      onPlayingChange?.(false);
    } else {
      a.play().then(() => {
        setPlaying(true);
        onPlayingChange?.(true);
      }).catch(() => setError(true));
    }
  };

  const fmtTime = (s: number) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60);
    return `${m}:${ss.toString().padStart(2, '0')}`;
  };

  const label = VARIANT_LABEL[variante];

  return (
    <div
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        padding: 0,
        background: 'transparent',
        border: 'none',
        textAlign: 'left',
        fontFamily: FONT,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div
        className="preview-card"
        data-selected={selected}
        style={{
          position: 'relative',
          padding: '22px 22px 18px',
          borderRadius: 24,
          background: selected
            ? 'linear-gradient(160deg, rgba(255,145,77,0.12) 0%, rgba(255,176,122,0.04) 100%), var(--surface-white)'
            : 'var(--surface-white)',
          border: selected ? '2px solid var(--gold)' : '1.5px solid var(--border-soft)',
          boxShadow: selected
            ? '0 18px 48px -12px rgba(255,145,77,0.40), 0 4px 18px rgba(255,145,77,0.18), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 4px 18px rgba(0,0,0,0.04)',
          transition: 'all 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          overflow: 'hidden',
          transform: selected ? 'translateY(-2px)' : 'none',
        }}
      >
        {selected && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              background:
                'radial-gradient(ellipse at 100% 0%, rgba(255,145,77,0.10), transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(255,176,122,0.06), transparent 55%)',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Header da card: ord + nome (script) */}
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span
              style={{
                fontFamily: FONT_DISPLAY,
                fontStyle: 'italic',
                fontWeight: 700,
                fontSize: 38,
                lineHeight: 1,
                letterSpacing: '-0.04em',
                color: selected ? 'var(--gold)' : 'var(--earth-light)',
                transition: 'color 0.3s',
              }}
            >
              {label.ord}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-soft)',
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
              }}
            >
              {label.longa}
            </span>
          </div>

          {/* Checkbox visual */}
          <div
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: selected ? '2px solid var(--gold)' : '1.5px solid var(--border-fade)',
              background: selected ? 'var(--gold)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.25s',
              flexShrink: 0,
            }}
          >
            {selected && (
              <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5.5L5 9.5L13 1.5" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>

        {/* Player + waveform */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <PlayButton playing={playing} onClick={handlePlay} disabled={!audioUrl || error} selected={selected} />
          <Waveform playing={playing} progress={progress} selected={selected} />
        </div>

        {/* Tempo + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ fontWeight: 500 }}>
            {error ? '⚠️ Não consegui carregar' : loaded ? `${fmtTime(current)} / ${fmtTime(duration)}` : 'Trecho de 45s'}
          </span>
          <span style={{ fontStyle: 'italic', opacity: 0.7 }}>pra {nomeHomenageada}</span>
        </div>

        {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}
      </div>

      <style>{`
        @keyframes inspiraWavePulse {
          0%, 100% { transform: scaleY(0.35); }
          50% { transform: scaleY(1); }
        }
        .preview-card[data-selected="true"] {
          animation: inspiraCardBreathe 4s ease-in-out infinite;
        }
        @keyframes inspiraCardBreathe {
          0%, 100% { box-shadow: 0 18px 48px -12px rgba(255,145,77,0.40), 0 4px 18px rgba(255,145,77,0.18), inset 0 1px 0 rgba(255,255,255,0.06); }
          50% { box-shadow: 0 22px 60px -12px rgba(255,145,77,0.50), 0 6px 22px rgba(255,145,77,0.24), inset 0 1px 0 rgba(255,255,255,0.06); }
        }
      `}</style>
    </div>
  );
}

function PlayButton({ playing, onClick, disabled, selected }: { playing: boolean; onClick: (e: React.MouseEvent) => void; disabled: boolean; selected: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={playing ? 'Pausar' : 'Tocar'}
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 54,
        height: 54,
        borderRadius: '50%',
        border: 'none',
        background: selected
          ? 'linear-gradient(135deg, var(--gold), var(--gold-hover))'
          : 'var(--earth)',
        color: '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        boxShadow: selected
          ? '0 8px 24px rgba(255,145,77,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
          : '0 6px 18px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.18s, box-shadow 0.18s',
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.94)')}
      onMouseUp={(e) => (e.currentTarget.style.transform = '')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
    >
      {playing ? (
        <svg width="16" height="18" viewBox="0 0 16 18" fill="currentColor">
          <rect x="0" y="0" width="5" height="18" rx="1.5" />
          <rect x="11" y="0" width="5" height="18" rx="1.5" />
        </svg>
      ) : (
        <svg width="18" height="20" viewBox="0 0 18 20" fill="currentColor" style={{ marginLeft: 3 }}>
          <path d="M2 1.4v17.2c0 1.1 1.2 1.7 2.2 1.1L17 11.1c.9-.6.9-1.9 0-2.5L4.2.3C3.2-.3 2 .3 2 1.4z" />
        </svg>
      )}
    </button>
  );
}

function Waveform({ playing, progress, selected }: { playing: boolean; progress: number; selected: boolean }) {
  // 36 barras de waveform "fake" — alturas pseudoaleatórias estáveis por índice
  const bars = Array.from({ length: 36 }, (_, i) => {
    const seed = (i * 9301 + 49297) % 233280;
    const r = seed / 233280;
    return 0.32 + r * 0.68;
  });
  const accent = selected ? 'var(--gold)' : 'var(--earth-light)';
  const dim = selected ? 'rgba(255,145,77,0.22)' : 'var(--border-fade)';
  return (
    <div style={{ flex: 1, position: 'relative', height: 44, display: 'flex', alignItems: 'center', gap: 3 }}>
      {bars.map((h, i) => {
        const filled = i / bars.length < progress;
        const baseHeight = h * 38;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              minWidth: 2,
              height: baseHeight,
              borderRadius: 2,
              background: filled ? accent : dim,
              transformOrigin: 'center',
              animation: playing ? `inspiraWavePulse ${0.7 + (i % 5) * 0.12}s ease-in-out infinite` : 'none',
              animationDelay: playing ? `${(i % 7) * 0.08}s` : '0s',
              transition: 'background 0.2s',
            }}
          />
        );
      })}
    </div>
  );
}
